import React from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import Testimonials from '../components/Testimonials';

const { FiShield, FiTrendingUp, FiZap, FiArrowRight } = FiIcons;

const Home = ({ viewMode }) => {
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: { opacity: 1, transition: { staggerChildren: 0.3, delayChildren: 0.2 } }
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 30 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.6, ease: "easeOut" } }
  };

  const stats = [
    { icon: FiShield, value: "30+", label: "Years Experience" },
    { icon: FiTrendingUp, value: "100+", label: "Security Audits" },
    { icon: FiZap, value: "50+", label: "Cloud Migrations" }
  ];

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="relative min-h-screen flex items-center justify-center gradient-bg overflow-hidden">
        {/* Animated Background Elements */}
        <div className="absolute inset-0">
          {[...Array(20)].map((_, i) => (
            <motion.div
              key={i}
              className="absolute w-2 h-2 bg-teal-400 rounded-full opacity-20"
              animate={{
                x: [0, 100, 0],
                y: [0, -100, 0],
                scale: [1, 1.5, 1],
              }}
              transition={{
                duration: 10 + i * 2,
                repeat: Infinity,
                delay: i * 0.5,
              }}
              style={{
                left: `${Math.random() * 100}%`,
                top: `${Math.random() * 100}%`,
              }}
            />
          ))}
        </div>

        <motion.div
          className="container mx-auto px-4 text-center text-white relative z-10"
          variants={containerVariants}
          initial="hidden"
          animate="visible"
        >
          <motion.div variants={itemVariants}>
            <motion.img
              src="https://cdn1.sharemyimage.com/smi/2026/01/18/GaryGlowCloseUp.png"
              alt="Gary Fagerholm"
              className="w-32 h-32 rounded-full mx-auto mb-6 border-4 border-teal-400 shadow-2xl object-cover"
              whileHover={{ scale: 1.1 }}
              transition={{ duration: 0.3 }}
            />
          </motion.div>

          <motion.h1
            variants={itemVariants}
            className="font-heading text-5xl md:text-7xl font-bold mb-4"
          >
            {viewMode === 'professional' ? (
              <>Digital <span className="text-yellow-400">Security</span> Expert</>
            ) : (
              <>Tech <span className="text-yellow-400">Enthusiast</span> & Explorer</>
            )}
          </motion.h1>

          <motion.p
            variants={itemVariants}
            className="text-xl md:text-2xl mb-8 text-gray-300 max-w-3xl mx-auto"
          >
            {viewMode === 'professional' ? (
              "A Lifetime of IT Security Expertise & Passion - Protecting digital assets with cutting-edge solutions."
            ) : (
              "Passionate about technology, adventure, and making the digital world safer - one system at a time!"
            )}
          </motion.p>

          <motion.div variants={itemVariants} className="flex flex-col sm:flex-row gap-4 justify-center mb-12">
            <Link
              to="/experience"
              className="inline-flex items-center px-8 py-4 bg-teal-600 hover:bg-teal-700 text-white font-bold rounded-lg transition-all duration-300 transform hover:scale-105 shadow-lg"
            >
              View My Experience <SafeIcon icon={FiArrowRight} className="ml-2 w-5 h-5" />
            </Link>
          </motion.div>

          <motion.div
            variants={itemVariants}
            className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-4xl mx-auto"
          >
            {stats.map((stat, index) => (
              <motion.div
                key={index}
                className="glass-effect rounded-xl p-6 text-center"
                whileHover={{ scale: 1.05, y: -5 }}
                transition={{ duration: 0.3 }}
              >
                <SafeIcon icon={stat.icon} className="w-8 h-8 mx-auto mb-4 text-yellow-400" />
                <div className="text-3xl font-bold font-heading mb-2">{stat.value}</div>
                <div className="text-gray-300">{stat.label}</div>
              </motion.div>
            ))}
          </motion.div>
        </motion.div>

        {/* Scroll Indicator */}
        <motion.div
          className="absolute bottom-8 left-1/2 transform -translate-x-1/2"
          animate={{ y: [0, 10, 0] }}
          transition={{ duration: 2, repeat: Infinity }}
        >
          <div className="w-6 h-10 border-2 border-white rounded-full flex justify-center">
            <div className="w-1 h-3 bg-white rounded-full mt-2"></div>
          </div>
        </motion.div>
      </section>

      {/* Quick Overview Section */}
      <section className="section-padding bg-white">
        <motion.div
          className="container mx-auto"
          initial={{ opacity: 0, y: 50 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          viewport={{ once: true }}
        >
          <div className="text-center mb-16">
            <h2 className="font-heading text-4xl font-bold mb-6 text-gray-900">
              {viewMode === 'professional' ? 'Security Excellence' : 'My Journey'}
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              {viewMode === 'professional' 
                ? "Specializing in enterprise security architecture, threat detection, and building resilient digital infrastructures." 
                : "From curious teenager taking apart computers to senior security engineer - it's been quite the adventure!"}
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {[
              { title: "Cybersecurity", desc: "Advanced threat detection & response" },
              { title: "Architecture", desc: "Secure system design & implementation" },
              { title: "Compliance", desc: "SOC 2, ISO 27001, GDPR expertise" },
              { title: "Leadership", desc: "Team building & strategic planning" }
            ].map((item, index) => (
              <motion.div
                key={index}
                className="bg-gradient-to-br from-gray-50 to-gray-100 rounded-xl p-6 hover:shadow-xl transition-all duration-300 border border-gray-100"
                whileHover={{ scale: 1.05, y: -5 }}
              >
                <h3 className="font-heading text-xl font-bold mb-3 text-gray-900">{item.title}</h3>
                <p className="text-gray-600">{item.desc}</p>
              </motion.div>
            ))}
          </div>
        </motion.div>
      </section>

      {/* Testimonials Section */}
      <Testimonials viewMode={viewMode} />
    </div>
  );
};

export default Home;